import { useCallback, useEffect } from "react";

import { OspToast } from "@ospweb/sdk-ui";
import { pubsub } from "utils";
import { useSnapshot } from "valtio";
import { pubSubKeyMap } from "../utils/pubSubKeyMap";
import { rootStore } from "../store";
import { useLoginModalProvider } from "./useLoginModalProvider";
import Translations from "@translate/hooks/useChatViewReportHandler";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

type handleViewReportParams = {
  id: string;
  symbol: string;
  type: "crypto" | "stock";
};

interface UseViewReportHandlerParams {
  streamLoading: boolean;
  onViewReport: (params: any) => void;
}

export const useChatViewReportHandler = ({
  onViewReport,
  streamLoading,
}: UseViewReportHandlerParams) => {
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { open } = useLoginModalProvider();
  const handleViewReport = useCallback(
    async ({ type, symbol, id }: handleViewReportParams) => {
      if (!isLoggedIn) {
        open?.();
        return;
      }

      if (streamLoading) {
        OspToast.error({
          content: I18n.t("streamLoading"),
        });
      }

      onViewReport({
        customParams: {
          cache_content: `Give me a clear overview of ${symbol}. Should I invest in it?`,
          content: I18n.t(
            "Give me a clear overview of %{var0}. Should I invest in it?",
            {
              var0: symbol,
            },
          ),
          type: `${type === "crypto" ? "token" : "stock"}:360:symbol#${symbol}+id#${id}`,
        },
      });
    },
    [isLoggedIn, streamLoading, onViewReport, open],
  );

  useEffect(() => {
    const off = pubsub.on(
      pubSubKeyMap.CHAT_360,
      async (params: handleViewReportParams) => {
        handleViewReport(params);
      },
    );
    return () => {
      off();
    };
  }, [handleViewReport]);
};
